import { useForm } from "react-hook-form";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import Dialog from "../../components/ui/Dialog";
import type { AuthRole } from "../../types/auth";

type RoleForm = {
  role: AuthRole;
};

interface Props {
  userEmail: string;
  currentRole: AuthRole;
  onSubmit: (values: RoleForm) => Promise<void>;
  onClose: () => void;
}

const RoleCard = ({
  title,
  tone,
  items,
}: {
  title: string;
  tone: "info" | "neutral";
  items: string[];
}) => {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-4">
      <div className="flex items-center gap-2">
        <Badge tone={tone} size="sm" label={title} />
      </div>
      <div className="mt-3 space-y-2">
        {items.map(item => (
          <p key={item} className="text-sm text-slate-600">{item}</p>
        ))}
      </div>
    </div>
  );
};

export default ({ userEmail, currentRole, onSubmit, onClose }: Props) => {
  const roleForm = useForm<RoleForm>({
    defaultValues: { role: currentRole },
  });

  const handleSubmit = roleForm.handleSubmit(async values => {
    await onSubmit(values);
  });

  return (
    <Dialog title="Update role" subtitle={userEmail} onClose={onClose}>
      <form className="space-y-5" onSubmit={event => void handleSubmit(event)}>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-slate-500">Role</label>
          <select
            {...roleForm.register("role", { required: true })}
            className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none transition-colors focus:border-indigo-500 focus:bg-white"
          >
            <option value="admin">Admin</option>
            <option value="team">Team Member</option>
          </select>
        </div>
        <div className="space-y-3">
          <RoleCard
            title="Admin"
            tone="info"
            items={[
              "Full access across the app",
              "Can manage team members and admins",
              "Can disable access, reset passwords, and update roles",
            ]}
          />
          <RoleCard
            title="Team Member"
            tone="neutral"
            items={[
              "Can use the product like a regular internal user",
              "Cannot manage team members or admins",
              "No access to the Teams management page",
            ]}
          />
        </div>
        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={roleForm.formState.isSubmitting}>
            {roleForm.formState.isSubmitting ? "Saving..." : "Save role"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
